import {
  Box,
  InputBaseComponentProps,
  menuClasses,
  Select as MuiSelect,
  Tooltip,
  Typography,
} from '@mui/material'
import { IconArrow } from 'assets/icons'
import { MenuOption } from 'elements/navigation'
import { useBoolean } from 'lib/hooks'
import { checkScrollToBottom } from 'lib/js-utils'
import { forwardRef } from 'react'
import { useMeasure } from 'react-use'

import { FormControl, FormControlWrapper } from '../form'
import { InputBase } from '../input-base'

export type Option = {
  value: string | number
  label: string | number
  disabled?: boolean
  tooltipText?: string
}

export type SelectProps = FormControlWrapper & {
  options: Array<Option>
  value: string | number
  onChange: (value: string | number) => void
  placeholder?: string
  disabled?: boolean
  loadingError?: boolean

  hasMoreOptions?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean

  noBorder?: boolean
  inputProps?: InputBaseComponentProps
}

export const Select = forwardRef(
  (
    {
      error,
      id,
      helperText,
      onRemove,
      required,
      label,
      loadingError,
      placeholder,
      value,
      onChange,
      disabled,
      onLoadMore,
      hasMoreOptions,
      isLoadingMore,
      options,
      noBorder,
      inputProps,
      ...rest
    }: SelectProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const [selectRef, { width }] = useMeasure()

    const open = useBoolean()

    return (
      <FormControl
        {...rest}
        select
        label={label}
        error={error}
        id={id}
        helperText={helperText}
        onRemove={onRemove}
        required={required}
      >
        <MuiSelect
          open={open.isTrue}
          onOpen={open.setTrue}
          onClose={open.setFalse}
          ref={selectRef}
          labelId={id ?? label}
          required={required}
          {...(noBorder && { sx: { border: 0 } })}
          inputProps={inputProps}
          input={
            <InputBase
              ref={ref}
              fullWidth
              error={error}
              required={required}
              sx={{
                ...(open.isTrue && {
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                }),
              }}
            />
          }
          IconComponent={IconArrow}
          displayEmpty={!loadingError}
          renderValue={
            placeholder && !value
              ? () => (
                  <Typography
                    color="text.secondary"
                    component="span"
                    variant="caption1"
                  >
                    {placeholder}
                  </Typography>
                )
              : undefined
          }
          value={value}
          onChange={event => {
            onChange(event.target.value)
          }}
          disabled={disabled}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            // this is essential for correct custom alignment
            style: { transform: 'translateX(0.5px)' },
            sx: {
              [`& .${menuClasses.paper}`]: {
                width: noBorder ? 'auto' : width ?? 'auto',
                borderRadius: '4px',
                outline: theme => `1px solid ${theme.palette.carribeanGreen}`,

                border: 'none',
                backgroundColor: 'alabaster',
                borderTopRightRadius: 0,
                borderTopLeftRadius: 0,
              },

              [`& .${menuClasses.list}`]: {
                overflowY: 'auto',
                padding: 0,
                maxHeight: theme => theme.spacing(24),
              },
            },

            MenuListProps: {
              onScroll: event => {
                if (onLoadMore && hasMoreOptions && !isLoadingMore) {
                  checkScrollToBottom(event, onLoadMore)
                }
              },
            },
          }}
        >
          {[...options].map(option => (
            <MenuOption
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.tooltipText ? (
                <Tooltip
                  placement="right"
                  title={
                    <Typography variant="caption3">
                      {option.tooltipText}
                    </Typography>
                  }
                >
                  <Box component="span" sx={{ pointerEvents: 'auto' }}>
                    {option.label}
                  </Box>
                </Tooltip>
              ) : (
                option.label
              )}
            </MenuOption>
          ))}
        </MuiSelect>
      </FormControl>
    )
  },
)
